import { Org, OrgMetadata, OrgUser } from "@/features/org/orgTypes";
import { api } from "./api";
import { DELETE, POST } from "./consts";
import { User } from "@/features/auth/authTypes";
import {
  IAccount,
  IPortfolio,
  IUserLogin,
} from "@pulse-ui/codegen/generated_ts/org_entity";

export const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------- /ORGS ---------- */
    // Get organizations for the authenticated user
    getOrgs: builder.query<Org, void>({
      query: () => "/api/account/v1/orgs",
      transformResponse: (response: { data: Org[] }) => {
        // The original promise returns res.data.data[0], so we replicate that behavior
        return response.data[0];
      },
      providesTags: ["Org"],
    }),

    // Get metadata for a specific organization
    getOrgMetadata: builder.query<OrgMetadata, string>({
      query: (orgId) => `/api/account/v1/orgs/${orgId}/metadata`,
      providesTags: (_, __, orgId) => [{ type: "OrgMetadata", id: orgId }],
    }),

    // Get users for a specific organization
    getOrgUsers: builder.query<OrgUser[], string>({
      query: (orgId) => `/api/account/v1/orgs/${orgId}/users`,
      providesTags: (_, __, orgId) => [{ type: "OrgUsers", id: orgId }],
    }),

    // Add user to organization
    addUser: builder.mutation<
      any,
      {
        orgId: string;
        firstname: string;
        lastname: string;
        email: string;
      }
    >({
      query: ({ orgId, firstname, lastname, email }) => ({
        url: `/api/account/v1/orgs/${orgId}/users`,
        method: POST,
        body: { firstname, lastname, email },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "OrgUsers", id: orgId },
      ],
    }),

    /* ---------- /USERS ---------- */
    // --- USER ---
    getUser: builder.query<User, void>({
      query: () => ({
        url: "/api/user/v1",
        responseHandler: async (response: Response) => {
          // First, read the response as text
          const text = await response.text();
          // Try to parse as JSON, but if it fails, return the text as-is
          try {
            return JSON.parse(text);
          } catch {
            // If it's not JSON, return the plain text string
            // transformResponse will handle converting it to a User object
            return text;
          }
        },
      }),
      providesTags: ["User"],
      transformResponse: (response: any): User => {
        // If response is already a User object, return it
        if (response && typeof response === "object" && "user_id" in response) {
          return response;
        }

        // If response is wrapped in a data property (like getOrgs)
        if (
          response?.data &&
          typeof response.data === "object" &&
          "user_id" in response.data
        ) {
          return response.data;
        }

        // If response is just a string (user_id), construct a minimal User object
        // This should be temporary - the API should return the full User object
        if (typeof response === "string") {
          const userId = response.trim().replace("\n", "");
          return {
            user_id: userId,
            admin: false,
            roles: [],
            authorized_portfolios: [],
            dropcopy_ip_addresses: [],
          };
        }

        // Fallback
        throw new Error("Unexpected response format from /api/user/v1");
      },
    }),
    // Get user login by org user id
    getUserLoginByOrgUserId: builder.query<IUserLogin, string>({
      query: (orgUserId) => `/api/account/v1/user_login/${orgUserId}`,
    }),
    // Delete user
    deleteUser: builder.mutation<void, { org_id: string; user_id: string }>({
      query: ({ org_id, user_id }) => ({
        url: `/api/account/v1/orgs/${org_id}/users/${user_id}`,
        method: DELETE,
      }),
      invalidatesTags: ["User"],
    }),

    /* ---------- /USER_GRID_STATES ---------- */
    // Get AG Grid preferences states for user
    getUserStates: builder.query<string, string>({
      query: (gridId) => `/api/account/v1/user_grid_state/${gridId}`,
      providesTags: (_, __, gridId) => [{ type: "Preferences", id: gridId }],
    }),
    // Save user state
    saveUserState: builder.mutation<void, { gridId: string; state: string }>({
      query: ({ gridId, state }) => ({
        url: `/api/account/v1/user_grid_state`,
        method: POST,
        body: { grid_id: gridId, display_name: gridId, grid_state: state },
      }),
    }),
    // Delete user state
    deleteUserState: builder.mutation<
      void,
      { grid_id: string; display_name: string }
    >({
      query: ({ grid_id, display_name }) => ({
        url: `/api/account/v1/user_grid_state`,
        method: DELETE,
        body: { grid_id, display_name },
      }),
    }),
    /* ---------- /PORTFOLIOS ---------- */
    // Get portfolios for a specific organization
    getPortfolios: builder.query<IPortfolio[], string>({
      query: (orgId) => `/api/account/v1/orgs/${orgId}/portfolios`,
      providesTags: ["OrgPortfolios"],
    }),
    // Get portfolio by id
    getPortfolio: builder.query<
      IPortfolio,
      { orgId: string; portfolioId: string }
    >({
      query: ({ orgId, portfolioId }) =>
        `/api/account/v1/orgs/${orgId}/portfolios/${portfolioId}`,
    }),
    /* ---------- /ACCOUNTS ---------- */
    // Get account
    getAccount: builder.query<
      IAccount,
      { portfolioId: string; accountId: string }
    >({
      query: ({ portfolioId, accountId }) =>
        `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
    }),
    // Get accounts by portfolio
    getAccountsByPortfolio: builder.query<IAccount[], string>({
      query: (portfolioId) =>
        `/api/account/v1/portfolios/${portfolioId}/venue_accounts`,
    }),
    /* ---------- /MISC ---------- */
    // Get IP addresses
    getIpAddresses: builder.query<string[], void>({
      query: () => "/api/account/v1/ips/dropcopy",
    }),
    // Get Venue codes
    getVenueCodes: builder.query<string[], void>({
      query: () => "/api/account/v1/venue_codes",
    }),
  }),
});

export const {
  useGetOrgsQuery,
  useGetOrgMetadataQuery,
  useGetOrgUsersQuery,
  useGetUserQuery,
  useGetUserLoginByOrgUserIdQuery,
  useDeleteUserMutation,
  useGetUserStatesQuery,
  useSaveUserStateMutation,
  useDeleteUserStateMutation,
  useGetPortfoliosQuery,
  useGetPortfolioQuery,
  useGetAccountQuery,
  useGetAccountsByPortfolioQuery,
  useGetIpAddressesQuery,
  useGetVenueCodesQuery,
} = accountApi;
